// Microphone recording and reply playback for the chat bar. Both run in the host page, so the
// microphone permission is the site's own (the browser asks on first use).
import { speechChunks, speechText } from './speech.js';

/**
 * A millisecond of silence, played inside a tap so iOS lets the same element play replies later.
 * As a blob: URL, like the replies, so a site's CSP needs only `media-src blob:`.
 */
const SILENCE_WAV = 'UklGRiwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQgAAACAgICAgICAgA==';
let silence = null;
const silenceUrl = () => (silence ??= URL.createObjectURL(new Blob([Uint8Array.from(atob(SILENCE_WAV), (c) => c.charCodeAt(0))], { type: 'audio/wav' })));

const RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

/** Microphone permission for the extension: 'granted' | 'denied' | 'prompt'. */
export async function micPermission() {
  try {
    return (await navigator.permissions.query({ name: 'microphone' })).state;
  } catch {
    return 'prompt';
  }
}

/** A sentence for a getUserMedia failure. */
export function micErrorText(e) {
  if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') return 'Microphone access is blocked for this site. Allow it from the icon at the left of the address bar, then try again.';
  if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') return 'No microphone found. Plug one in or check your system sound settings.';
  if (e?.name === 'NotReadableError' || e?.name === 'AbortError') return 'The microphone is busy or unavailable. Close other apps that use it and try again.';
  return `Could not start the microphone: ${e?.message ?? e}`;
}

/** One recording at a time: start(), then stop() for the audio or cancel(). */
export class Recorder {
  constructor() {
    this.ctx = null;
  }

  /** onLevel(rms 0..1, elapsedMs) is called while recording. Rejects like getUserMedia. */
  async start({ onLevel } = {}) {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    try {
      const mimeType = RECORDING_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
      this.rec = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
      this.chunks = [];
      this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
      this.rec.start(250);
    } catch (e) {
      for (const t of this.stream.getTracks()) t.stop(); // don't leave the microphone on
      this.stream = null;
      throw e;
    }
    this.startedAt = performance.now();
    // Peak sample, to tell a muted microphone (all zeros) from one that works. Stays null
    // when the audio context can't run (no user gesture in this panel yet): no check then.
    this.peak = null;
    try {
      this.ctx ??= new AudioContext();
      if (this.ctx.state !== 'running') this.ctx.resume().catch(() => {});
      this.source = this.ctx.createMediaStreamSource(this.stream);
      const analyser = this.ctx.createAnalyser();
      analyser.fftSize = 32768; // ~0.7 s, so polling every 60 ms sees every sample
      this.source.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      const recent = 1024; // the level shown is the last ~20 ms
      this.meter = setInterval(() => {
        if (this.ctx.state !== 'running') return;
        analyser.getFloatTimeDomainData(buf);
        let peak = 0;
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = buf[i];
          if (v > peak) peak = v;
          else if (-v > peak) peak = -v;
          if (i >= buf.length - recent) sum += v * v;
        }
        this.peak = Math.max(this.peak ?? 0, peak);
        onLevel?.(Math.sqrt(sum / recent), performance.now() - this.startedAt);
      }, 60);
    } catch {
      /* no meter */
    }
  }

  async #finish() {
    clearInterval(this.meter);
    this.source?.disconnect();
    this.source = null;
    this.ctx?.suspend().catch(() => {}); // resumed by the next start()
    const rec = this.rec;
    if (rec && rec.state !== 'inactive') {
      await new Promise((resolve) => {
        rec.onstop = resolve;
        rec.stop();
      });
    }
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    this.stream = null;
    this.rec = null;
    return rec;
  }

  /** Resolves { blob, filename, ms, peak }. */
  async stop() {
    const ms = performance.now() - this.startedAt;
    const rec = await this.#finish();
    const type = (rec?.mimeType || 'audio/webm').split(';')[0];
    const ext = type.includes('ogg') ? 'ogg' : type.includes('mp4') ? 'm4a' : 'webm';
    return { blob: new Blob(this.chunks, { type }), filename: `speech.${ext}`, ms, peak: this.peak };
  }

  async cancel() {
    await this.#finish();
    this.chunks = [];
  }
}

/**
 * Reads text aloud through `synthesize({ settings, text, signal }) -> Blob`, one chunk at a
 * time, fetching the next chunk while the current one plays.
 * onState({ speaking }) on start/finish, onState({ error }) on failure, and
 * onState({ blocked: resume }) when the browser wants a click before it plays audio.
 */
export class Speaker {
  constructor({ synthesize, onState }) {
    this.synthesize = synthesize;
    this.onState = onState;
    this.queue = [];
    this.gen = 0;
    this.running = false;
    this.audio = null; // the element while it plays a reply
    this.el = null; // one element for every reply, unlocked by unlock()
    this.unlocked = false;
    this.controller = null;
    this.settle = null;
  }

  get speaking() {
    return this.running;
  }

  /**
   * Call from a tap or key press (sending, the mic): iOS only lets an element play sound it
   * started inside a gesture, so the element replies play in is started here once, silently.
   */
  unlock() {
    if (this.unlocked || this.running || typeof Audio === 'undefined') return;
    this.unlocked = true;
    const el = this.#element();
    el.src = silenceUrl();
    el.play().catch(() => (this.unlocked = false));
  }

  #element() {
    this.el ??= new Audio();
    return this.el;
  }

  speak(markdown, settings) {
    for (const text of speechChunks(speechText(markdown))) this.queue.push({ text, settings, blob: null });
    if (this.queue.length && !this.running) void this.#run();
  }

  stop() {
    this.gen++;
    this.queue = [];
    this.controller?.abort();
    this.controller = null;
    if (this.audio) {
      this.audio.onended = this.audio.onerror = null;
      this.audio.pause();
      URL.revokeObjectURL(this.audio.src);
      this.audio = null;
    }
    this.settle?.();
    this.settle = null;
    if (this.running) {
      this.running = false;
      this.onState({ speaking: false });
    }
  }

  #fetch(item) {
    item.blob ??= this.synthesize({ settings: item.settings, text: item.text, signal: this.controller.signal });
    item.blob.catch(() => {}); // awaited in #run; this only marks it handled
    return item.blob;
  }

  async #run() {
    const gen = this.gen;
    this.running = true;
    this.controller = new AbortController();
    this.onState({ speaking: true });
    try {
      while (this.queue.length && gen === this.gen) {
        const [item, next] = this.queue;
        this.#fetch(item);
        if (next) this.#fetch(next);
        const blob = await item.blob;
        if (gen !== this.gen) return;
        await this.#play(blob, item.settings);
        if (gen !== this.gen) return;
        this.queue.shift();
      }
    } catch (e) {
      if (gen === this.gen && e.name !== 'AbortError') {
        this.stop();
        this.onState({ error: e });
      }
    } finally {
      if (gen === this.gen) {
        this.running = false;
        this.controller = null;
        this.onState({ speaking: false });
      }
    }
  }

  #play(blob, settings) {
    return new Promise((resolve, reject) => {
      const audio = this.#element();
      audio.onended = audio.onerror = null;
      audio.src = URL.createObjectURL(blob);
      audio.defaultPlaybackRate = audio.playbackRate = Number(settings.ttsSpeed) || 1;
      audio.preservesPitch = true;
      this.audio = audio;
      const done = () => {
        audio.onended = audio.onerror = null;
        if (this.audio === audio) this.audio = null;
        URL.revokeObjectURL(audio.src);
        this.settle = null;
        resolve();
      };
      this.settle = resolve;
      audio.onended = done;
      audio.onerror = () => reject(new Error('The speech audio could not be played.'));
      audio.play().catch((e) => {
        if (e.name === 'AbortError') return; // stop() paused it
        if (e.name !== 'NotAllowedError') return reject(e);
        this.onState({ blocked: () => audio.play().catch(reject) });
      });
    });
  }
}
